import { useDebouncedCallback } from "@mantine/hooks";
import { type ChangeEvent, memo, useState } from "react";
import { t } from "ttag";

import { FixedSizeIcon, Group, Loader, TextInput } from "metabase/ui";
import { SEARCH_DEBOUNCE_DURATION } from "metabase/utils/constants";
import { FilterOptionsPicker } from "metabase-enterprise/dependencies/components/FilterOptionsPicker";
import type { DependencyFilterOptions } from "metabase-enterprise/dependencies/types";
import {
  areFilterOptionsEqual,
  getSearchQuery,
} from "metabase-enterprise/dependencies/utils";

import type { DependencyDiagnosticsMode } from "../types";
import { getAvailableGroupTypes, getDefaultFilterOptions } from "../utils";

type DiagnosticsFilterBarProps = {
  mode: DependencyDiagnosticsMode;
  query?: string;
  filterOptions: DependencyFilterOptions;
  isFetching: boolean;
  isLoading: boolean;
  onQueryChange: (query: string | undefined) => void;
  onFilterOptionsChange: (filterOptions: DependencyFilterOptions) => void;
};

export const DiagnosticsFilterBar = memo(function DiagnosticsFilterBar({
  mode,
  query,
  filterOptions,
  isFetching,
  isLoading,
  onQueryChange,
  onFilterOptionsChange,
}: DiagnosticsFilterBarProps) {
  const [searchValue, setSearchValue] = useState(query ?? "");
  const hasLoader = isFetching && !isLoading;
  const hasDefaultFilterOptions = areFilterOptionsEqual(
    filterOptions,
    getDefaultFilterOptions(mode),
  );

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newSearchValue = event.target.value;
    setSearchValue(newSearchValue);
    handleSearchDebounce(newSearchValue);
  };

  const handleSearchDebounce = useDebouncedCallback(
    (newSearchValue: string) => {
      const newQuery = getSearchQuery(newSearchValue);
      onQueryChange(newQuery);
    },
    SEARCH_DEBOUNCE_DURATION,
  );

  const handleFilterOptionsChange = (
    newFilterOptions: DependencyFilterOptions,
  ) => {
    onFilterOptionsChange(newFilterOptions);
  };

  return (
    <Group gap="md" align="center" wrap="nowrap">
      <TextInput
        value={searchValue}
        placeholder={t`Search…`}
        flex={1}
        leftSection={<FixedSizeIcon name="search" />}
        rightSection={hasLoader ? <Loader size="sm" /> : undefined}
        data-testid="dependency-list-search-input"
        onChange={handleSearchChange}
      />
      <FilterOptionsPicker
        filterOptions={filterOptions}
        availableGroupTypes={getAvailableGroupTypes(mode)}
        isDisabled={isLoading}
        hasDefaultFilterOptions={hasDefaultFilterOptions}
        onFilterOptionsChange={handleFilterOptionsChange}
      />
    </Group>
  );
});
